import type { AccountSettingsTab, TabVisibilityContext } from '../types/account-settings-layout.types'

export enum AccountSettingsTabKey {
  Profile = 'profile',
  Account = 'account',
  SocialMedia = 'social-media',
  Certifications = 'certifications',
  BillingSubscriptions = 'billing-subscriptions',
  Chats = 'chats',
  Meeting = 'meeting',
  LanguageSettings = 'language-settings',
  Notifications = 'notifications',
  Location = 'location',
}

// Account Settings Tab Configuration
export const ACCOUNT_SETTINGS_TABS: AccountSettingsTab[] = [
  {
    id: AccountSettingsTabKey.Profile,
    name: 'Profile',
    tab: AccountSettingsTabKey.Profile,
    component: 'Profile',
    order: 1,
  },
  {
    id: AccountSettingsTabKey.Account,
    name: 'Account',
    tab: AccountSettingsTabKey.Account,
    component: 'Account',
    order: 2,
  },
  {
    id: AccountSettingsTabKey.SocialMedia,
    name: 'Social Media',
    tab: AccountSettingsTabKey.SocialMedia,
    component: 'SocialMedia',
    order: 4,
  },
  {
    id: AccountSettingsTabKey.Certifications,
    name: 'Certificates & Badges',
    tab: AccountSettingsTabKey.Certifications,
    component: 'Certifications',
    order: 5,
  },
  {
    id: AccountSettingsTabKey.BillingSubscriptions,
    name: 'Billing and Subscriptions',
    tab: AccountSettingsTabKey.BillingSubscriptions,
    component: 'BillingSubscriptions',
    order: 6,
    shouldShowTab: ({ isGokollab, locationPermissions: lp }) => lp.subscriptionsEnabled !== false && !isGokollab,
  },
  {
    id: AccountSettingsTabKey.Chats,
    name: 'Chats',
    tab: AccountSettingsTabKey.Chats,
    component: 'Chats',
    order: 7,
  },
  {
    id: AccountSettingsTabKey.Meeting,
    name: 'Meeting',
    tab: AccountSettingsTabKey.Meeting,
    component: 'Meeting',
    order: 3,
    shouldShowTab: ctx => ctx.isGroupAdminOrOwner,
  },
  {
    id: AccountSettingsTabKey.LanguageSettings,
    name: 'Language Settings',
    tab: AccountSettingsTabKey.LanguageSettings,
    component: 'LanguageSettings',
    order: 8,
    shouldShowTab: ctx => ctx.isMobile !== false,
  },
  {
    id: AccountSettingsTabKey.Notifications,
    name: 'Notifications',
    tab: AccountSettingsTabKey.Notifications,
    component: 'NotificationPreferences',
    order: 9,
    shouldShowTab: ({ isGokollab, locationPermissions: lp }) => isGokollab || lp.communitiesEnabled || lp.membershipEnabled,
  },
  {
    id: AccountSettingsTabKey.Location,
    name: 'Location & Timezone',
    tab: AccountSettingsTabKey.Location,
    component: 'Location',
    order: 10,
    shouldShowTab: ctx => ctx.isMobile === true,
  },
]

export function isAccountSettingsTabVisible(tab: AccountSettingsTab, ctx: TabVisibilityContext): boolean {
  if (!tab.shouldShowTab) {
    return true
  }
  try {
    return tab.shouldShowTab(ctx)
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error)
    console.error(`[account-settings] shouldShowTab failed for tab "${tab.tab}":`, message)
    return false
  }
}

/** Tabs visible for the given runtime context (GoKollab, permissions, mobile layout, BE flags, etc.). */
export function filterVisibleAccountSettingsTabs(tabs: AccountSettingsTab[], ctx: TabVisibilityContext): AccountSettingsTab[] {
  return tabs.filter(tab => isAccountSettingsTabVisible(tab, ctx))
}

// Default configuration
export const DEFAULT_ACCOUNT_SETTINGS_CONFIG = {
  defaultTab: AccountSettingsTabKey.Profile,
  sidebarConfig: {
    showToggle: false,
    togglePosition: 'header' as const,
    colorScheme: 'background' as const,
    collapsed: false,
  },
  layout: {
    sidebarCols: 3,
    contentCols: 9,
    showTopBar: true,
    showHeader: true,
  },
}

// Tab ordering and filtering utilities
export const getOrderedTabs = (tabs: AccountSettingsTab[] = ACCOUNT_SETTINGS_TABS) => {
  return [...tabs].sort((a, b) => (a.order || 0) - (b.order || 0))
}

export const getTabById = (id: string, tabs: AccountSettingsTab[] = ACCOUNT_SETTINGS_TABS) => {
  return tabs.find(tab => tab.id === id || tab.tab === id)
}

export const getValidTabIds = (tabs: AccountSettingsTab[] = ACCOUNT_SETTINGS_TABS) => {
  return tabs.map(tab => tab.tab)
}

// Backwards compatibility
export const accountSettingsTabs = ACCOUNT_SETTINGS_TABS
